/**
 * FlowRunHistory Component
 *
 * Shows execution history for flows.
 * Displays run details with filtering and export capabilities.
 *
 * @component
 */

import React, { useState, useMemo } from 'react';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Pause,
  RefreshCw,
  Filter,
  Download,
  ChevronDown,
  ChevronRight,
  Search,
  Calendar,
  User,
  Activity
} from 'lucide-react';
import {
  StandardButton,
  StandardCard,
  StandardDataTable,
  StandardModal,
  StandardSelect,
  StandardInput,
  StandardEmptyState,
  useToast
} from '../../standard';
import type { FlowRun, FlowRunStatus, ExecutedElement } from '../../../types/flowAutomation';

// ============================================
// INTERFACES
// ============================================

interface FlowRunHistoryProps {
  flowId?: string;
  runs: FlowRun[];
  onRefresh: () => void;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

const getStatusIcon = (status: FlowRunStatus, size: number = 16) => {
  switch (status) {
    case 'completed':
      return <CheckCircle size={size} className="text-green-500" />;
    case 'failed':
      return <XCircle size={size} className="text-red-500" />;
    case 'running':
      return <RefreshCw size={size} className="text-blue-500 animate-spin" />;
    case 'waiting':
      return <Clock size={size} className="text-yellow-500" />;
    case 'paused':
      return <Pause size={size} className="text-gray-500" />;
    default:
      return <AlertCircle size={size} className="text-gray-500" />;
  }
};

const getStatusBadge = (status: FlowRunStatus) => {
  const styles: Record<FlowRunStatus, string> = {
    completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    running: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    waiting: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    paused: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
      {getStatusIcon(status, 12)}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const formatDuration = (ms: number | undefined) => {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
};

const getTriggerLabel = (triggerType: string) => {
  const labels: Record<string, string> = {
    user: 'Manual',
    schedule: 'Scheduled',
    record: 'Record Change',
    process: 'Process Builder',
    api: 'API',
    event: 'Platform Event'
  };
  return labels[triggerType] || triggerType;
};

// ============================================
// RUN DETAIL MODAL COMPONENT
// ============================================

interface RunDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  run: FlowRun | null;
}

const RunDetailModal: React.FC<RunDetailModalProps> = ({ isOpen, onClose, run }) => {
  const [expandedElements, setExpandedElements] = useState<Set<string>>(new Set());

  if (!run) return null;

  const toggleElement = (elementId: string) => {
    const newExpanded = new Set(expandedElements);
    if (newExpanded.has(elementId)) {
      newExpanded.delete(elementId);
    } else {
      newExpanded.add(elementId);
    }
    setExpandedElements(newExpanded);
  };

  const getElementStatusIcon = (status: ExecutedElement['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle size={14} className="text-green-500" />;
      case 'error':
        return <XCircle size={14} className="text-red-500" />;
      case 'skipped':
        return <AlertCircle size={14} className="text-gray-400" />;
    }
  };

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Run Details"
      size="lg"
    >
      <div className="p-6 space-y-6">
        {/* Run Summary */}
        <StandardCard className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
              <div className="mt-1">{getStatusBadge(run.status)}</div>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Started</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-1">
                {formatDate(run.startTime)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Duration</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-1">
                {formatDuration(run.duration)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Trigger</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-1">
                {getTriggerLabel(run.triggerType)}
              </p>
            </div>
          </div>
        </StandardCard>

        {/* Error Message */}
        {run.errorMessage && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-start gap-3">
              <XCircle size={20} className="text-red-500 mt-0.5" />
              <div>
                <p className="font-medium text-red-700 dark:text-red-400">Error</p>
                <p className="text-sm text-red-600 dark:text-red-300 mt-1">
                  {run.errorMessage}
                </p>
                {run.errorElement && (
                  <p className="text-xs text-red-500 dark:text-red-400 mt-2">
                    Failed at element: {run.errorElement}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Context Record */}
        {run.contextRecordId && (
          <StandardCard className="p-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              Context Record
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Object: </span>
                <span className="text-gray-900 dark:text-gray-100">{run.contextObjectType}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Record: </span>
                <span className="text-gray-900 dark:text-gray-100 font-mono">
                  {run.contextRecordId}
                </span>
              </div>
            </div>
          </StandardCard>
        )}

        {/* Elements Executed */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            Elements Executed ({run.elementsExecuted.length})
          </h4>
          <div className="space-y-2">
            {run.elementsExecuted.map((element, index) => (
              <div
                key={`${element.elementId}-${index}`}
                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleElement(element.elementId)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  {expandedElements.has(element.elementId) ? (
                    <ChevronDown size={14} className="text-gray-400" />
                  ) : (
                    <ChevronRight size={14} className="text-gray-400" />
                  )}
                  {getElementStatusIcon(element.status)}
                  <span className="flex-1 text-left text-sm font-medium text-gray-900 dark:text-gray-100">
                    {element.elementName}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                    {element.elementType.replace(/_/g, ' ')}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDuration(new Date(element.endTime).getTime() - new Date(element.startTime).getTime())}
                  </span>
                </button>

                {expandedElements.has(element.elementId) && (
                  <div className="px-10 pb-3 text-sm space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Start: </span>
                        <span className="text-gray-700 dark:text-gray-300">
                          {formatDate(element.startTime)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">End: </span>
                        <span className="text-gray-700 dark:text-gray-300">
                          {formatDate(element.endTime)}
                        </span>
                      </div>
                    </div>

                    {element.errorMessage && (
                      <p className="text-red-600 dark:text-red-400 text-xs">
                        Error: {element.errorMessage}
                      </p>
                    )}

                    {element.inputValues && Object.keys(element.inputValues).length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Inputs:</p>
                        <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto">
                          {JSON.stringify(element.inputValues, null, 2)}
                        </pre>
                      </div>
                    )}

                    {element.outputValues && Object.keys(element.outputValues).length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Outputs:</p>
                        <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto">
                          {JSON.stringify(element.outputValues, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Metadata */}
        <StandardCard className="p-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
            Run Information
          </h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Run ID: </span>
              <span className="text-gray-900 dark:text-gray-100 font-mono text-xs">
                {run.id}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Flow Version: </span>
              <span className="text-gray-900 dark:text-gray-100">v{run.flowVersion}</span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Triggered By: </span>
              <span className="text-gray-900 dark:text-gray-100">{run.triggeredByName}</span>
            </div>
          </div>
        </StandardCard>
      </div>
    </StandardModal>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const FlowRunHistory: React.FC<FlowRunHistoryProps> = ({
  flowId,
  runs,
  onRefresh
}) => {
  const { addToast } = useToast();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRun, setSelectedRun] = useState<FlowRun | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Filter runs
  const filteredRuns = useMemo(() => {
    return runs.filter(run => {
      // Status filter
      if (statusFilter !== 'all' && run.status !== statusFilter) return false;

      // Date filter
      if (dateFilter !== 'all') {
        const runDate = new Date(run.startTime);
        const now = new Date();
        const diffDays = Math.floor((now.getTime() - runDate.getTime()) / (1000 * 60 * 60 * 24));

        if (dateFilter === 'today' && diffDays > 0) return false;
        if (dateFilter === 'week' && diffDays > 7) return false;
        if (dateFilter === 'month' && diffDays > 30) return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          run.id.toLowerCase().includes(query) ||
          run.flowName.toLowerCase().includes(query) ||
          run.triggeredByName.toLowerCase().includes(query) ||
          (run.contextRecordId && run.contextRecordId.toLowerCase().includes(query))
        );
      }

      return true;
    });
  }, [runs, statusFilter, dateFilter, searchQuery]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = filteredRuns.length;
    const completed = filteredRuns.filter(r => r.status === 'completed').length;
    const failed = filteredRuns.filter(r => r.status === 'failed').length;
    const avgDuration = total > 0
      ? filteredRuns.reduce((sum, r) => sum + (r.duration || 0), 0) / total
      : 0;

    return { total, completed, failed, avgDuration };
  }, [filteredRuns]);

  const handleViewDetails = (run: FlowRun) => {
    setSelectedRun(run);
    setShowDetailModal(true);
  };

  const handleExportCSV = () => {
    const headers = ['Run ID', 'Flow Name', 'Status', 'Started', 'Duration', 'Triggered By', 'Error'];
    const rows = filteredRuns.map(run => [
      run.id,
      run.flowName,
      run.status,
      formatDate(run.startTime),
      formatDuration(run.duration),
      run.triggeredByName,
      run.errorMessage || ''
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flow-runs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    addToast({
      type: 'success',
      title: 'Export Complete',
      message: `Exported ${filteredRuns.length} runs to CSV`
    });
  };

  // Table columns
  const columns = [
    {
      key: 'status',
      label: 'Status',
      render: (_value: unknown, run: FlowRun) => getStatusBadge(run.status)
    },
    {
      key: 'flowName',
      label: 'Flow',
      render: (_value: unknown, run: FlowRun) => (
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {run.flowName}
        </span>
      )
    },
    {
      key: 'startTime',
      label: 'Started',
      render: (_value: unknown, run: FlowRun) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {formatDate(run.startTime)}
        </span>
      )
    },
    {
      key: 'duration',
      label: 'Duration',
      render: (_value: unknown, run: FlowRun) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {formatDuration(run.duration)}
        </span>
      )
    },
    {
      key: 'trigger',
      label: 'Trigger',
      render: (_value: unknown, run: FlowRun) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {getTriggerLabel(run.triggerType)}
        </span>
      )
    },
    {
      key: 'triggeredBy',
      label: 'Triggered By',
      render: (_value: unknown, run: FlowRun) => (
        <div className="flex items-center gap-2 text-sm">
          <User size={14} className="text-gray-400" />
          <span className="text-gray-700 dark:text-gray-300">
            {run.triggeredByName}
          </span>
        </div>
      )
    },
    {
      key: 'elements',
      label: 'Elements',
      render: (_value: unknown, run: FlowRun) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {run.elementsExecuted.length}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_value: unknown, run: FlowRun) => (
        <StandardButton
          variant="ghost"
          size="sm"
          onClick={() => handleViewDetails(run)}
        >
          View Details
        </StandardButton>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StandardCard className="p-4">
          <div className="flex items-center gap-3">
            <Activity size={20} className="text-blue-500" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Runs</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {stats.total}
              </p>
            </div>
          </div>
        </StandardCard>

        <StandardCard className="p-4">
          <div className="flex items-center gap-3">
            <CheckCircle size={20} className="text-green-500" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Completed</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {stats.completed}
              </p>
            </div>
          </div>
        </StandardCard>

        <StandardCard className="p-4">
          <div className="flex items-center gap-3">
            <XCircle size={20} className="text-red-500" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Failed</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {stats.failed}
              </p>
            </div>
          </div>
        </StandardCard>

        <StandardCard className="p-4">
          <div className="flex items-center gap-3">
            <Clock size={20} className="text-purple-500" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Avg Duration</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {formatDuration(stats.avgDuration)}
              </p>
            </div>
          </div>
        </StandardCard>
      </div>

      {/* Filters */}
      <StandardCard className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by run ID, flow name, or record ID..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-3">
            <StandardSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'completed', label: 'Completed' },
                { value: 'failed', label: 'Failed' },
                { value: 'running', label: 'Running' },
                { value: 'waiting', label: 'Waiting' },
                { value: 'paused', label: 'Paused' }
              ]}
              className="w-36"
            />
            <StandardSelect
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Time' },
                { value: 'today', label: 'Today' },
                { value: 'week', label: 'Last 7 Days' },
                { value: 'month', label: 'Last 30 Days' }
              ]}
              className="w-36"
            />
            <StandardButton
              variant="secondary"
              onClick={onRefresh}
              leftIcon={<RefreshCw size={16} />}
            >
              Refresh
            </StandardButton>
            <StandardButton
              variant="secondary"
              onClick={handleExportCSV}
              leftIcon={<Download size={16} />}
              disabled={filteredRuns.length === 0}
            >
              Export
            </StandardButton>
          </div>
        </div>
      </StandardCard>

      {/* Runs Table */}
      {filteredRuns.length === 0 ? (
        <StandardEmptyState
          title="No Runs Found"
          message={
            searchQuery || statusFilter !== 'all' || dateFilter !== 'all'
              ? "No runs match your current filters. Try adjusting your search criteria."
              : "This flow hasn't been executed yet. Runs will appear here once the flow is triggered."
          }
        />
      ) : (
        <StandardCard>
          <StandardDataTable
            data={filteredRuns}
            columns={columns}
          />
        </StandardCard>
      )}

      {/* Run Detail Modal */}
      <RunDetailModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedRun(null);
        }}
        run={selectedRun}
      />
    </div>
  );
};

export default FlowRunHistory;
